import type { ConsentStatus, CrossDomainConfig, StorageData } from '../types';
import type { StorageAdapter } from '../core/adapters';
import { normalizeVisitorId } from '../core/visitorId';

const STORAGE_KEY = 'crelora_mark_data';
const OUTBOX_KEY = 'crelora_mark_outbox';
const COOKIE_NAME = 'crelora_mark_vid';

interface BrowserStorageOptions extends CrossDomainConfig {
  storageKey?: string;
  outboxKey?: string;
  /**
   * Trusted first-party visitor id to adopt when no id is stored yet.
   */
  seed_visitor_id?: string;
}

interface BridgeMessage {
  type: 'MARK_SYNC_REQUEST' | 'MARK_SYNC_RESPONSE' | 'MARK_SYNC_UPDATE';
  visitorId?: string;
}

export class BrowserStorage implements StorageAdapter {
  private data: StorageData;
  private readonly storageKey: string;
  private readonly outboxKey: string;
  private readonly options: BrowserStorageOptions;
  private bridgeFrame?: HTMLIFrameElement;
  private bridgeReady = false;
  private bridgeOrigin?: string;

  constructor(options?: BrowserStorageOptions) {
    this.options = options ?? {};
    this.storageKey = this.options.storageKey ?? STORAGE_KEY;
    this.outboxKey = this.options.outboxKey ?? OUTBOX_KEY;
    if (typeof window === 'undefined') {
      this.data = {};
      return;
    }
    this.data = this.load();

    const seedVisitorId = normalizeVisitorId(this.options.seed_visitor_id);
    if (!this.data.visitor_id && seedVisitorId) {
      this.data.visitor_id = seedVisitorId;
      this.save();
    }

    if (!this.data.visitor_id) {
      this.data.visitor_id = this.generateUUID();
      this.save();
    }

    if (typeof window !== 'undefined' && this.options.bridge?.url) {
      this.setupBridge(this.options.bridge);
    }
    window.addEventListener('storage', this.handleStorageEvent);
  }

  public getVisitorId(): string | undefined {
    return this.data.visitor_id;
  }

  public getLastClickId(): string | undefined {
    return this.data.last_click_id;
  }

  public getCampaignId(): string | undefined {
    return this.data.campaign_id;
  }

  public getQueryParams(): Record<string, string> | undefined {
    return this.data.query_params;
  }

  public getConsentStatus(): ConsentStatus | undefined {
    return this.data.consent_status;
  }

  public getUserId(): string | undefined {
    return this.data.user_id;
  }

  public getSessionId(): string | undefined {
    return this.data.session_id;
  }

  public getSessionStartedAt(): string | undefined {
    return this.data.session_started_at;
  }

  public getLastActivityAt(): string | undefined {
    return this.data.last_activity_at;
  }

  public getInternal(): boolean | undefined {
    return this.data.is_internal;
  }

  public setInternal(value: boolean) {
    if (value) {
      this.update({ is_internal: true });
    } else {
      this.update({ is_internal: undefined });
    }
  }

  public update(updates: Partial<StorageData>) {
    this.data = { ...this.data, ...updates };
    this.save();
  }

  public setConsentStatus(status: ConsentStatus) {
    this.data.consent_status = status;
    this.save();
  }

  public clearAttribution() {
    this.update({
      last_click_id: undefined,
      campaign_id: undefined,
      query_params: undefined,
    });
  }

  public clearCookieVisitorId() {
    this.setCookie(COOKIE_NAME, '', -1);
  }

  public rotateVisitorId() {
    this.update({ visitor_id: this.generateUUID() });
  }

  /**
   * Adopts or replaces the stored visitor id with a trusted first-party value.
   * No-op when the id is invalid or already set to the same value.
   */
  public setVisitorId(visitorId: string): boolean {
    const normalized = normalizeVisitorId(visitorId);
    if (!normalized) {
      return false;
    }
    if (this.data.visitor_id === normalized) {
      return true;
    }
    this.update({ visitor_id: normalized });
    return true;
  }

  public getOutbox(): unknown[] {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(this.outboxKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  public setOutbox(items: unknown[]) {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(this.outboxKey, JSON.stringify(items.slice(-200)));
    } catch {
      // Ignore storage failures caused by privacy settings.
    }
  }

  private load(): StorageData {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Ignore storage failures caused by privacy settings.
    }

    const cookieVid = this.getCookie(COOKIE_NAME);
    if (cookieVid) {
      return { visitor_id: cookieVid };
    }

    return {};
  }

  private save() {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.data));
    } catch {
      // Ignore storage failures caused by privacy settings.
    }

    if (this.data.visitor_id && this.isCookieEnabled()) {
      this.setCookie(COOKIE_NAME, this.data.visitor_id, 365);
      if (this.options.bridge?.url && this.bridgeReady) {
        this.postBridgeMessage({
          type: 'MARK_SYNC_UPDATE',
          visitorId: this.data.visitor_id,
        });
      }
    }
  }

  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }
    try {
      const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
      if (match) return match[2];
    } catch {
      // Ignore cookie access errors.
    }
    return null;
  }

  private setCookie(name: string, value: string, days: number) {
    if (typeof document === 'undefined') {
      return;
    }
    try {
      const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
      const domainPart = this.options.cookie_domain
        ? `;domain=${this.options.cookie_domain}`
        : '';
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${domainPart}`;
    } catch {
      // Ignore cookie access errors.
    }
  }

  private generateUUID(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = ((Date.now() + Math.random() * 16) % 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private isCookieEnabled() {
    if (!this.options.mode) return true;
    return this.options.mode === 'single' || this.options.mode === 'subdomain';
  }

  private setupBridge(bridge: NonNullable<CrossDomainConfig['bridge']>) {
    if (typeof document === 'undefined') return;
    let bridgeUrl: URL;
    try {
      bridgeUrl = new URL(bridge.url, window.location.href);
    } catch {
      return;
    }
    this.bridgeOrigin = bridgeUrl.origin;
    this.bridgeFrame = document.createElement('iframe');
    this.bridgeFrame.style.display = 'none';
    this.bridgeFrame.setAttribute('aria-hidden', 'true');
    this.bridgeFrame.src = bridgeUrl.toString();

    const timeout = bridge.timeout_ms ?? 3000;
    const timer = setTimeout(() => {
      window.removeEventListener('message', this.handleBridgeMessage);
    }, timeout);

    window.addEventListener('message', this.handleBridgeMessage);
    const appendFrame = () => {
      if (!this.bridgeFrame) return;
      if (!document.body) return;
      document.body.appendChild(this.bridgeFrame);
    };
    if (document.body) {
      appendFrame();
    } else {
      document.addEventListener('DOMContentLoaded', appendFrame, { once: true });
    }

    this.bridgeFrame.addEventListener('load', () => {
      clearTimeout(timer);
      this.bridgeReady = true;
      this.postBridgeMessage({
        type: 'MARK_SYNC_REQUEST',
        visitorId: this.data.visitor_id,
      });
    });
  }

  private handleBridgeMessage = (event: MessageEvent<BridgeMessage>) => {
    if (!this.bridgeFrame?.contentWindow || event.source !== this.bridgeFrame.contentWindow) {
      return;
    }
    if (!this.isDomainAllowed(event.origin)) {
      return;
    }

    const message = event.data;
    if (!message || !message.type) return;

    if (message.type === 'MARK_SYNC_RESPONSE' && message.visitorId) {
      if (message.visitorId !== this.data.visitor_id) {
        this.data.visitor_id = message.visitorId;
        this.save();
      }
    }
  };

  private postBridgeMessage(message: BridgeMessage) {
    if (!this.bridgeFrame?.contentWindow) return;
    this.bridgeFrame.contentWindow.postMessage(message, this.bridgeOrigin ?? '*');
  }

  private isDomainAllowed(origin: string) {
    try {
      const originUrl = new URL(origin);
      if (this.options.allowed_domains?.length) {
        return this.options.allowed_domains.some((domain) =>
          originUrl.hostname === domain || originUrl.hostname.endsWith(`.${domain}`)
        );
      }
      if (!this.bridgeOrigin) {
        return false;
      }
      const bridgeUrl = new URL(this.bridgeOrigin);
      return originUrl.hostname === bridgeUrl.hostname;
    } catch {
      return false;
    }
  }

  private handleStorageEvent = (event: StorageEvent) => {
    if (event.key !== this.storageKey || typeof event.newValue !== 'string') {
      return;
    }
    try {
      this.data = JSON.parse(event.newValue);
    } catch {
      // Ignore malformed storage updates.
    }
  };
}

